import type { ReactNode } from 'react';
import { useFormatting } from './Formatting.js';
import { Tooltip } from './Tooltip.js';
import './Numeric.css';

export interface MoneyProps {
  /**
   * Amount in the currency's **minor unit** (cents, pence, …). Integer, never a
   * float: `19.99` in binary floating point is not 19.99, and money that is
   * wrong in the third decimal place is money that is wrong. The API models
   * every amount this way (`*_cents` in openapi.yaml), so this component takes
   * what the API gives it rather than expecting a conversion at each call site.
   */
  amountMinorUnits: number | null | undefined;
  /** ISO 4217, e.g. `USD`. */
  currency?: string | null;
  locale?: string;
  /**
   * The formula and inputs behind a *derived* figure. 08 §7 requires that every
   * currency figure discloses how it was produced, and a11y.md requires that
   * disclosure be reachable by keyboard — not hover-only. Omit for a plain
   * price, which is a fact rather than an estimate.
   */
  formula?: ReactNode;
  /**
   * Rendered when the amount is null. There is deliberately no default: a
   * missing figure means something specific ("no rate configured" is not
   * "insufficient data" is not "free"), and a component-level guess would
   * flatten distinctions the product spent ADR-045 keeping apart.
   */
  fallback?: ReactNode;
}

/**
 * A currency amount (design-system catalogue, Tier 3).
 *
 * Deliberately refuses to invent a number. A null amount renders the caller's
 * `fallback`, never `0` and never an em dash chosen here — because
 * [ADR-045] keeps `no_rate_configured` distinct from `insufficient_data`
 * precisely so the two can prompt different actions, and a component that
 * printed "—" for both would undo that at the last step.
 *
 * `Intl.NumberFormat` does the formatting, so minor-unit scaling is correct per
 * currency without a hardcoded /100: JPY has no minor unit, KWD has three
 * decimal places. Dividing by 100 everywhere is the bug this avoids.
 */
export function Money({
  amountMinorUnits,
  currency,
  locale,
  formula,
  fallback = null,
}: MoneyProps) {
  const formatting = useFormatting();
  /* ⚠️ The org's currency is a FALLBACK, never an override: a figure that names its own currency
     names it for a reason, and silently reformatting it as the org's would change what the number
     means. `null` from both stays null — no substituted default, per F25's rule. */
  const resolved = currency ?? formatting.currency;
  if (amountMinorUnits == null || !resolved) return <>{fallback}</>;

  const fmt = new Intl.NumberFormat(locale ?? formatting.locale, { style: 'currency', currency: resolved });
  // digits === 0 for JPY, 3 for KWD, 2 for most. Never assume 2.
  const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
  const major = amountMinorUnits / 10 ** digits;
  const formatted = fmt.format(major);

  // `dpr-numeric` (tabular figures) rather than a bare span: a currency column
  // whose digits do not share an advance width does not line up, and the
  // prototype sets tabular-nums on every money figure it renders (audit F-9).
  if (!formula) return <span className="dpr-numeric">{formatted}</span>;

  // tabIndex makes the disclosure keyboard-reachable; Tooltip renders on focus
  // as well as hover, which is what a11y.md §5 requires and what the prototype
  // got wrong by using hover alone.
  return (
    <Tooltip content={formula}>
      <span className="dpr-numeric" tabIndex={0}>
        {formatted}
      </span>
    </Tooltip>
  );
}
